import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import "./Vender.css";
import { FaPlus, FaEye, FaEdit, FaPhoneAlt, FaSearch, FaTrash, FaStore } from "react-icons/fa";

export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => { fetchVendors(); }, []);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/showAllvendor");
      setVendors(response.data || []);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this vendor?")) return;
    try {
      await axiosInstance.delete(`/vendors/${id}`);
      setVendors(vendors.filter(v => v.id !== id));
    } catch {
      alert("Failed to delete vendor");
    }
  };

  const filteredVendors = vendors.filter(vendor =>
    vendor.vendorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.phoneNo?.includes(searchTerm)
  );

  const initials = (name) => {
    if (!name) return "?";
    return name.trim().split(" ").slice(0, 2).map(w => w[0]?.toUpperCase() || "").join("");
  };

  const avatarColor = (name) => {
    const colors = ["#2C4A7C","#7C3D2C","#2C7C5A","#5A2C7C","#7C6B2C","#2C627C"];
    if (!name) return colors[0];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return colors[Math.abs(h) % colors.length];
  };

  return (
    <div className="vnd-page">

      {/* ── PAGE HEADER ── */}
      <div className="vnd-hero">
        <div className="vnd-hero-left">
          <div className="vnd-hero-icon"><FaStore /></div>
          <div>
            <h1 className="vnd-title">Vendor Management</h1>
            <p className="vnd-subtitle">
              {vendors.length} vendor{vendors.length !== 1 ? "s" : ""} registered
            </p>
          </div>
        </div>
        <div className="vnd-hero-actions">
          <button className="vnd-btn-outline" onClick={() => navigate('/materialRegistration')}>
            + Register Material
          </button>
          <button className="vnd-btn-primary" onClick={() => navigate('/add-venders')}>
            <FaPlus style={{ marginRight: 6, fontSize: 11 }} />
            Add Vendor
          </button>
        </div>
      </div>

      {/* ── STATS BAR ── */}
      <div className="vnd-stats">
        <div className="vnd-stat">
          <span className="vnd-stat-val">{vendors.length}</span>
          <span className="vnd-stat-label">Total Vendors</span>
        </div>
        <div className="vnd-stat-divider" />
        <div className="vnd-stat">
          <span className="vnd-stat-val">{filteredVendors.length}</span>
          <span className="vnd-stat-label">Showing</span>
        </div>
      </div>

      {/* ── SEARCH ── */}
      <div className="vnd-search-row">
        <div className="vnd-search-box">
          <FaSearch className="vnd-search-icon" />
          <input
            type="text"
            placeholder="Search by vendor name or phone number…"
            className="vnd-search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="vnd-search-clear" onClick={() => setSearchTerm("")}>✕</button>
          )}
        </div>
      </div>

      {/* ── TABLE CARD ── */}
      <div className="vnd-table-card">
        {loading ? (
          <div className="vnd-state-box">
            <div className="vnd-spinner" />
            <p>Loading vendors…</p>
          </div>
        ) : filteredVendors.length === 0 ? (
          <div className="vnd-state-box">
            <FaStore style={{ fontSize: 36, opacity: .15, marginBottom: 12 }} />
            <p style={{ fontWeight: 600, marginBottom: 4 }}>No vendors found</p>
            {searchTerm && <small>Try a different search term</small>}
          </div>
        ) : (
          <table className="vnd-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Vendor</th>
                <th>Phone Number</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVendors.map((vendor, idx) => (
                <tr key={vendor.id} style={{ animationDelay: `${idx * 35}ms` }}>
                  <td className="vnd-idx">{idx + 1}</td>
                  <td>
                    <div className="vnd-name-cell">
                      <div
                        className="vnd-avatar"
                        style={{ background: avatarColor(vendor.vendorName) }}
                      >
                        {initials(vendor.vendorName)}
                      </div>
                      <div className="vnd-name-text">
                        <span className="vnd-name">{vendor.vendorName}</span>
                        <span className="vnd-id-tag">ID #{vendor.id}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="vnd-phone">
                      <FaPhoneAlt className="vnd-phone-icon" />
                      {vendor.phoneNo || "—"}
                    </div>
                  </td>
                  <td>
                    <div className="vnd-actions">
                      <button
                        className="vnd-action-btn vnd-btn-view"
                        title="View Materials"
                        onClick={() => navigate(`/material/${vendor.id}`)}
                      >
                        <FaEye />
                        <span>View</span>
                      </button>
                      <button
                        className="vnd-action-btn vnd-btn-edit"
                        title="Edit Vendor"
                        onClick={() => navigate(`/add-venders/${vendor.id}`)}
                      >
                        <FaEdit />
                        <span>Edit</span>
                      </button>
                      <button
                        className="vnd-action-btn vnd-btn-delete"
                        title="Delete Vendor"
                        onClick={() => handleDelete(vendor.id)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="vnd-footer-note">
        Showing {filteredVendors.length} of {vendors.length} vendors
      </p>
    </div>
  );
}